import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getAccessToken } from '@/utils/jwt';

export interface NotificationItem {
  id: string;
  title: string;
  message: string | null;
  severity: 'error' | 'warning' | 'info';
  channel: string;
  source_type: string;
  source_id: string;
  project_id: string | null;
  is_read: boolean;
  created_at: string;
}

interface NotificationsResponse {
  items: NotificationItem[];
  total: number;
  limit: number;
  offset: number;
}

interface GetNotificationsParams {
  is_read?: boolean;
  limit?: number;
  offset?: number;
}

export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Notification', 'NotificationCount'],
  endpoints: (builder) => ({
    getNotifications: builder.query<NotificationsResponse, GetNotificationsParams | undefined>({
      query: (params) => ({
        url: '/notifications',
        params,
      }),
      providesTags: ['Notification'],
    }),
    getUnreadCount: builder.query<{ count: number }, undefined>({
      query: () => '/notifications/unread-count',
      providesTags: ['NotificationCount'],
    }),
    markRead: builder.mutation<undefined, string>({
      query: (id) => ({ url: `/notifications/${id}/read`, method: 'PATCH' }),
      invalidatesTags: ['Notification', 'NotificationCount'],
    }),
    markAllRead: builder.mutation<undefined, undefined>({
      query: () => ({ url: '/notifications/mark-all-read', method: 'POST' }),
      invalidatesTags: ['Notification', 'NotificationCount'],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkReadMutation,
  useMarkAllReadMutation,
} = notificationsApi;
